import { APIClient, HttpClient } from "@/utils/httpClient";
import { defineStore } from "pinia";
import router from "@/router";
import { campaignStore } from "./campaignStore";
import { pendingEventStore } from "./pendingEventStore";
import { v4 as uuidv4 } from "uuid";
import {
    recoveryMessage,
    resolveLoginRecoveryFailure,
} from "@/utils/loginRecovery";
import type { LoginResult } from "@/utils/loginRecovery";
import { applyCorrelationIds } from "@/utils/correlationIds";

/**
 * Status the API returns when the environment's sign-up policy refuses an
 * address (dev restricts self-signup to internal domains). It travels with an
 * HTTP 403, so axios rejects and the body lands in the request's catch block.
 */
const EMAIL_NOT_PERMITTED_STATUS = "EMAIL-NOT-PERMITTED";
/** Used only if the API sends the status without a message of its own. */
const EMAIL_NOT_PERMITTED_FALLBACK =
    "This email address is not permitted on this environment";

interface IAuthStoreState {
    campaignId: string | null;
    email: string | null;
    userId: string | null;
    walletId: string | null;
    otc: string | null;
    pageAfterAuth: boolean;
    userUUID: string | null;
    sessionId: string | null;
    journeyId: string | null;
    logging: boolean | null;
    termsAcceptedAt: string | null;
    termsAcceptedVersion: string | null;
    lastKnownGps: {
        lat: number;
        lon: number;
        accuracy: number | null;
        capturedAt: string;
    } | null;
}

export const authStore = defineStore("authStore", {
    persist: true,
    state: () =>
        ({
            campaignId: null,
            email: null,
            userId: null,
            walletId: null,
            otc: null,
            pageAfterAuth: false,
            userUUID: null,
            sessionId: null,
            journeyId: null,
            logging: true,
            termsAcceptedAt: null,
            termsAcceptedVersion: null,
            lastKnownGps: null,
        } as IAuthStoreState),
    getters: {
        getCampaignID(state: IAuthStoreState): string | null {
            return state.campaignId;
        },
        getEmail(state: IAuthStoreState): string | null {
            return state.email ? state.email.toLowerCase() : null;
        },
        getWalletID(state: IAuthStoreState): string | null {
            return state.walletId;
        },
        getUserID(state: IAuthStoreState): string | null {
            return state.userId;
        },
        getPageAfterAuth(state: IAuthStoreState): boolean {
            return state.pageAfterAuth;
        },
        getOtc(state: IAuthStoreState): string | null {
            return state.otc;
        },
        getUserUUID(state: IAuthStoreState): string | null {
            return state.userUUID;
        },
        getSessionID(state: IAuthStoreState): string | null {
            return state.sessionId;
        },
        getJourneyID(state: IAuthStoreState): string | null {
            return state.journeyId;
        },
        getLogging(state: IAuthStoreState): boolean | null {
            return state.logging;
        },
        getTermsAcceptedAt(state: IAuthStoreState): string | null {
            return state.termsAcceptedAt;
        },
        getTermsAcceptedVersion(state: IAuthStoreState): string | null {
            return state.termsAcceptedVersion;
        },
        getLastKnownGps(state: IAuthStoreState) {
            return state.lastKnownGps;
        },
    },
    actions: {
        saveOTC(otc: string) {
            this.otc = otc;
        },
        saveCampaignID(campaignId: string) {
            if (this.campaignId && this.campaignId !== campaignId) {
                // Campaign switched: clear old credentials
                this.email = null;
                this.userId = null;
                this.walletId = null;
                this.otc = null;
                localStorage.removeItem("AccessToken");
                localStorage.removeItem("RefreshToken");
            }
            this.campaignId = campaignId;
        },
        saveEmail(email: string) {
            this.email = email;
        },
        /**
         * Forget the previous login before a new code is requested.
         *
         * Registration and sign-in overwrite userId / walletId with the new
         * user's values, but the previous session's tokens stayed behind in
         * localStorage. The start-up checks then treated that stale token as
         * a live login and bounced the user past Enter PIN, and the React
         * experience app (same storage) failed every call with old token +
         * new ids. A code request starts a fresh session, so nothing from the
         * old one may survive it. The email is kept: the request itself
         * reads it to report a retry.
         */
        resetSessionForCodeRequest() {
            localStorage.removeItem("AccessToken");
            localStorage.removeItem("RefreshToken");
            this.userId = null;
            this.walletId = null;
            this.otc = null;
            this.pageAfterAuth = false;
        },
        saveWalletID(walletId: string) {
            this.walletId = walletId;
        },
        saveUserID(userId: string) {
            this.userId = userId;
        },
        savePageAfterAuth(pageAfterAuth: boolean) {
            this.pageAfterAuth = pageAfterAuth;
        },
        saveUserUUID(userUUID: string) {
            this.userUUID = userUUID;
        },
        saveSessionID(sessionId: string) {
            this.sessionId = sessionId;
        },
        saveJourneyID(journeyId: string) {
            this.journeyId = journeyId;
        },
        saveTermsAudit(params: {
            acceptedAt: string;
            acceptedTermsVersion: string;
        }) {
            this.termsAcceptedAt = params.acceptedAt;
            this.termsAcceptedVersion = params.acceptedTermsVersion;
        },
        recordTermsAcceptance(accepted: boolean, version?: string | null) {
            if (!accepted) {
                return;
            }

            this.termsAcceptedAt = new Date().toISOString();
            this.termsAcceptedVersion =
                version ||
                (this.campaignId ? `campaign:${this.campaignId}:default-terms` : "default-terms");
        },
        saveLastKnownGps(params: {
            lat: number;
            lon: number;
            accuracy?: number | null;
        }) {
            this.lastKnownGps = {
                lat: params.lat,
                lon: params.lon,
                accuracy: params.accuracy ?? null,
                capturedAt: new Date().toISOString(),
            };
        },
        ensureCorrelationIds() {
            if (!this.sessionId) {
                this.sessionId = uuidv4();
            }
            if (!this.journeyId) {
                this.journeyId = uuidv4();
            }
        },
        startJourney() {
            this.ensureCorrelationIds();
            this.journeyId = uuidv4();
            return this.journeyId;
        },
        logout() {
            router.push(`/${this.campaignId}`).catch((error) => {
                console.error("logout navigation failed", {
                    errorType: error instanceof Error ? error.name : "UnknownError",
                });
            });
            this.email = null;
            this.userId = null;
            this.walletId = null;
            this.journeyId = uuidv4();
            localStorage.clear();
        },
        saveStopLogging() {
            this.logging = false;
            const loggingInterval = setInterval(() => {
                this.logging = true;
                clearInterval(loggingInterval);
            }, 1800000);
        },
        buildCanonicalMetadata(options?: {
            includeTermsAudit?: boolean;
            includeGps?: boolean;
            path?: string | null;
        }): Record<string, unknown> {
            const metadata: Record<string, unknown> = {
                path: options?.path ?? window.location.pathname,
                referrer: document.referrer || null,
                userAgent: navigator.userAgent,
            };

            if (options?.includeTermsAudit && this.termsAcceptedAt && this.termsAcceptedVersion) {
                metadata.acceptedAt = this.termsAcceptedAt;
                metadata.acceptedTermsVersion = this.termsAcceptedVersion;
                metadata.termsAndConditionsCheck = true;
            }

            if (options?.includeGps !== false && this.lastKnownGps) {
                metadata.gps = {
                    lat: this.lastKnownGps.lat,
                    lon: this.lastKnownGps.lon,
                    accuracy: this.lastKnownGps.accuracy,
                    capturedAt: this.lastKnownGps.capturedAt,
                };
            }

            return metadata;
        },
        enrichPayloadWithCanonicalContext(
            payload: Record<string, any>,
            options?: {
                includeTermsAudit?: boolean;
                includeGps?: boolean;
                path?: string | null;
                guidCorrelationOnly?: boolean;
            }
        ) {
            this.ensureCorrelationIds();
            payload.requestId = payload.requestId || uuidv4();
            applyCorrelationIds(
                payload,
                { sessionId: this.sessionId, journeyId: this.journeyId },
                { guidOnly: options?.guidCorrelationOnly }
            );
            payload.userAgent = payload.userAgent || navigator.userAgent;

            const metadata = this.buildCanonicalMetadata(options);
            payload.metadata = {
                ...(payload.metadata && typeof payload.metadata === "object" ? payload.metadata : {}),
                ...metadata,
            };

            if (options?.includeTermsAudit && this.termsAcceptedAt && this.termsAcceptedVersion) {
                payload.acceptedAt = payload.acceptedAt || this.termsAcceptedAt;
                payload.acceptedTermsVersion =
                    payload.acceptedTermsVersion || this.termsAcceptedVersion;
            }

            if (options?.includeGps !== false && this.lastKnownGps) {
                payload.gps = payload.gps || {
                    lat: this.lastKnownGps.lat,
                    lon: this.lastKnownGps.lon,
                    accuracy: this.lastKnownGps.accuracy,
                };
            }

            return payload;
        },
        async dispatchGetCampaignID(subDomain: string) {
            try {
                const response = await APIClient.get(
                    `/campaigns/subdomain/${subDomain}`
                );
                if (response.success) {
                    this.campaignId = response.data.id;
                    return this.campaignId;
                } else {
                    if (response?.data?.status == "CAMPAIGN-ENDED") {
                        router
                            .replace(`/${this.campaignId}/campaign-over`)
                            .catch(() => {
                                //
                            });
                    }
                }
            } catch (error) {
                console.error("campaign lookup failed", {
                    errorType: error instanceof Error ? error.name : "UnknownError",
                });
                return;
            }
        },
        async registerLoginCode(obj: any): Promise<boolean> {
            this.ensureCorrelationIds();
            const endpoint = `/campaigns/${this.campaignId}/users/register`;
            const resendPin = document.getElementById("resend-pin");
            let originalResendText = "";
            if (resendPin) {
                originalResendText = resendPin!.innerText;
                resendPin!.innerText = "Sending new code";
            }

            obj.email = obj.email.toLowerCase();
            obj.email = obj.email.trim();
            try {
                obj.identifier = this.userUUID;
                this.enrichPayloadWithCanonicalContext(obj, {
                    includeTermsAudit: Boolean(obj.termsAndConditionsCheck),
                    guidCorrelationOnly: true,
                });
            } catch (error) {
                console.error("error getting userUUID");
            }

            this.resetSessionForCodeRequest();

            try {
                const response = await APIClient.post(endpoint, obj);

                if (response.success) {
                    this.saveEmail(obj.email);
                    this.saveWalletID(response.data.walletId);
                    this.saveUserID(response.data.userId);
                    if (resendPin) {
                        setTimeout(() => {
                            resendPin!.innerText = originalResendText;
                        }, 1000);
                    }
                    return true;
                } else {
                    if (response?.data?.status == "USER-REGISTERED") {
                        // Returning user: fall through to a sign-in code request.
                        // That request can fail under the login-recovery
                        // contract (rate limit, blocked, internal), and the
                        // registration page has no other place to say so.
                        const signin = await this.signinLoginCode({
                            email: obj.email,
                        });
                        if (!signin.success) {
                            alert(recoveryMessage(signin));
                            return false;
                        }
                        // The caller pushes to Enter PIN, so the register
                        // page stays in history and back returns to it.
                        return true;
                    }
                    if (response?.data?.status == "CAMPAIGN-ENDED") {
                        router
                            .replace(`/${this.campaignId}/campaign-over`)
                            .catch(() => {
                                //
                            });
                    }
                    if (response?.data?.status == "CAMPAIGN-PAUSED") {
                        router
                            .replace(`/${this.campaignId}/campaign-paused`)
                            .catch(() => {
                                //
                            });
                    }
                    return false;
                }
            } catch (error: any) {
                if (
                    error.response?.data?.message?.toLowerCase() ==
                    "email blocked"
                ) {
                    return false;
                }
                // The environment's sign-up policy can refuse this address
                // (e.g. dev only accepts internal domains). The API answers
                // 403 with status EMAIL-NOT-PERMITTED, so axios rejects and it
                // arrives here rather than in the success:false branch above.
                // Show the server's own wording — the user needs to know to
                // try a different address, not to "try again".
                const errorBody = error.response?.data;
                if (
                    errorBody?.data?.status == EMAIL_NOT_PERMITTED_STATUS ||
                    errorBody?.status == EMAIL_NOT_PERMITTED_STATUS
                ) {
                    const serverMessage =
                        typeof errorBody?.message === "string"
                            ? errorBody.message.trim()
                            : "";
                    alert(serverMessage || EMAIL_NOT_PERMITTED_FALLBACK);
                    return false;
                }
                alert("An error occured, please try again");
                console.error("registration request failed", {
                    errorType: error instanceof Error ? error.name : "UnknownError",
                });
                return false;
            }
        },
        async signinLoginCode(obj: { email: string | null }): Promise<LoginResult> {
            this.ensureCorrelationIds();
            const endpoint = `/campaigns/${this.campaignId}/users/login/request`;
            if (typeof obj.email !== "string" || obj.email.trim() === "") {
                return {
                    success: false,
                    status: "LOGIN-REQUEST-INVALID",
                    recoveryAction: "RESTART_SIGN_IN",
                    contract: "canonical",
                };
            }

            const request = {
                email: obj.email.toLowerCase().trim(),
                retry: Boolean(this.email),
            };
            this.saveEmail(request.email);
            this.enrichPayloadWithCanonicalContext(request, { guidCorrelationOnly: true });
            this.resetSessionForCodeRequest();

            try {
                const response = await APIClient.post(endpoint, request);

                if (response.success) {
                    this.saveEmail(request.email);
                    this.saveWalletID(response.data.walletId);
                    this.saveUserID(response.data.userId);
                    // Navigation belongs to the caller (sign-up and sign-in
                    // both push to Enter PIN; resend stays put). A replace
                    // here erased the register page from history, so back
                    // from Enter PIN skipped to whatever came before it.
                    return { success: true };
                } else {
                    if (response?.data?.status == "USER-NOT-REGISTERED") {
                        alert(
                            "You are not registered. Redirecting to to the registration page."
                        );
                        router.replace(`/${this.campaignId}`).catch(() => {
                            //
                        });
                    }
                    if (response?.data?.status == "CAMPAIGN-ENDED") {
                        router.replace(`/${this.campaignId}/campaign-over`);
                    }
                    if (response?.data?.status == "CAMPAIGN-PAUSED") {
                        router.replace(`/${this.campaignId}/campaign-paused`);
                    }

                    const result = resolveLoginRecoveryFailure(response, "request");
                    if (result.contract === "legacy_contract_fallback") {
                        console.warn("login recovery used legacy_contract_fallback", {
                            status: result.status,
                        });
                    }
                    return result;
                }
            } catch (error) {
                const result = resolveLoginRecoveryFailure(error, "request");
                if (result.contract === "legacy_contract_fallback") {
                    console.warn("login recovery used legacy_contract_fallback", {
                        status: result.status,
                    });
                }
                return result;
            }
        },
        async login(code: number): Promise<LoginResult> {
            this.ensureCorrelationIds();
            const endpoint = `/campaigns/${this.campaignId}/users/login`;
            if (
                !this.email ||
                !Number.isInteger(code) ||
                code < 1000 ||
                code > 9999
            ) {
                return {
                    success: false,
                    status: "LOGIN-REQUEST-INVALID",
                    recoveryAction: "RESTART_SIGN_IN",
                    contract: "canonical",
                };
            }
            try {
                const response = await APIClient.post(endpoint, {
                    email: this.email,
                    code,
                    ...this.enrichPayloadWithCanonicalContext({}, { guidCorrelationOnly: true }),
                });
                if (response.success) {
                    localStorage.setItem(
                        "AccessToken",
                        response.data.accessToken
                    );
                    localStorage.setItem(
                        "RefreshToken",
                        response.data.refreshToken
                    );
                    this.saveWalletID(response.data.walletId);
                    pendingEventStore().startPendingEvents();
                    return { success: true };
                } else {
                    if (response?.data?.status == "CAMPAIGN-ENDED") {
                        router
                            .replace(`/${this.campaignId}/campaign-over`)
                            .catch(() => {
                                //
                            });
                    }
                    if (response?.data?.status == "CAMPAIGN-PAUSED") {
                        router
                            .replace(`/${this.campaignId}/campaign-paused`)
                            .catch(() => {
                                //
                            });
                    }
                    const result = resolveLoginRecoveryFailure(response, "login");
                    if (result.contract === "legacy_contract_fallback") {
                        console.warn("login recovery used legacy_contract_fallback", {
                            status: result.status,
                        });
                    }
                    return result;
                }
            } catch (error) {
                const result = resolveLoginRecoveryFailure(error, "login");
                if (result.contract === "legacy_contract_fallback") {
                    console.warn("login recovery used legacy_contract_fallback", {
                        status: result.status,
                    });
                }
                return result;
            }
        },
        async checkAuthenticated(): Promise<boolean> {
            const token = localStorage.getItem("RefreshToken");
            const accessToken = localStorage.getItem("AccessToken");

            if (token && !HttpClient.checkExpired(accessToken as string)) {
                try {
                    const response = await APIClient.refreshToken(token);

                    const campaign = campaignStore().campaign;
                    // const campaign: Campaign = store.getters["campaignStore/getItem"];

                    if (response.success) {
                        const prevWalletID = authStore().getWalletID;
                        if (
                            prevWalletID != null &&
                            prevWalletID != "" &&
                            prevWalletID != response.data.walletId
                        ) {
                            authStore().savePageAfterAuth(false);
                        }

                        let pageAfterAuth = campaign?.pageAfterAuth;
                        console.log(authStore().getPageAfterAuth);
                        if (
                            (!pageAfterAuth || pageAfterAuth == "") &&
                            !this.pageAfterAuth
                        ) {
                            pageAfterAuth = "vault";
                        }
                        this.saveWalletID(response.data.walletId);

                        // Only redirect if user is on unauthenticated pages or hasn't been redirected yet
                        const currentRouteName = router.currentRoute.value.name;

                        const unauthenticatedPages = [
                            "Register",
                            "SignIn",
                            "Enter Pin",
                        ];
                        const currentPath = window.location.pathname;

                        console.log(
                            "checkAuthenticated: Current route:",
                            currentRouteName,
                            "Path:",
                            currentPath
                        );
                        console.log(
                            "checkAuthenticated: pageAfterAuth flag:",
                            this.pageAfterAuth
                        );

                        if (
                            !this.pageAfterAuth &&
                            unauthenticatedPages.includes(
                                currentRouteName as string
                            )
                        ) {
                            // User is on an unauthenticated page and hasn't been redirected yet
                            this.savePageAfterAuth(true);
                            console.log(
                                "checkAuthenticated: Redirecting from unauthenticated page to:",
                                pageAfterAuth
                            );
                            router
                                .replace({
                                    path: `/${this.campaignId}/${pageAfterAuth}`,
                                })
                                .catch(() => {
                                    //
                                });
                        } else if (
                            unauthenticatedPages.includes(
                                currentRouteName as string
                            )
                        ) {
                            // User is on an unauthenticated page but has been redirected before
                            this.savePageAfterAuth(true);
                            console.log(
                                "checkAuthenticated: Redirecting from unauthenticated page to:",
                                pageAfterAuth
                            );
                            router
                                .replace({
                                    path: `/${this.campaignId}/${pageAfterAuth}`,
                                })
                                .catch(() => {
                                    //
                                });
                        } else {
                            console.log("currentPath", currentPath);
                            // User is on an authenticated page (like Prize) - don't redirect
                            if (currentPath == `/${this.campaignId}/autologin`) {
                                router
                                    .push({
                                        path: `/${this.campaignId}/${pageAfterAuth ? pageAfterAuth : "vault"}`,
                                    })
                                    .catch((error) => {
                                        console.error("authenticated navigation failed", {
                                            errorType:
                                                error instanceof Error
                                                    ? error.name
                                                    : "UnknownError",
                                        });
                                    });
                            } else {
                                console.log(
                                    "checkAuthenticated: User already on authenticated page - not redirecting"
                                );
                            }
                        }
                        return true;
                    } else {
                        if (response?.data?.status == "CAMPAIGN-ENDED") {
                            router
                                .replace(`/${this.campaignId}/campaign-over`)
                                .catch(() => {
                                    //
                                });
                        }
                        if (response?.data?.status == "CAMPAIGN-PAUSED") {
                            router
                                .replace(`/${this.campaignId}/campaign-paused`)
                                .catch(() => {
                                    //
                                });
                        }
                        return false;
                    }
                } catch (error) {
                    console.error("authentication refresh failed", {
                        errorType: error instanceof Error ? error.name : "UnknownError",
                    });
                    return false;
                }
            }
            return false;
        },
        async autoRegister(autoReg: { email: string; queryString: string }) {
            const endpoint = `/campaigns/${this.campaignId}/users/autoregister?${autoReg.queryString}`;

            try {
                const response = await APIClient.get(endpoint);
                if (response.success) {
                    this.saveEmail(autoReg.email);
                    this.saveWalletID(response.data.walletId);
                    this.saveUserID(response.data.userId);
                    router
                        .replace(`/${this.campaignId}/enter-pin`)
                        .catch(() => {
                            //
                        });
                } else {
                    if (response?.data?.status == "USER-REGISTERED") {
                        alert(
                            "You have already registered, redirecting to sign in page"
                        );
                        router
                            .replace(`/${this.campaignId}/sign-in`)
                            .catch(() => {
                                //
                            });
                    }
                    // TODO: Alert something failed
                    if (response?.data?.status == "CAMPAIGN-ENDED") {
                        router
                            .replace(`/${this.campaignId}/campaign-over`)
                            .catch(() => {
                                //
                            });
                    }
                    if (response?.data?.status == "CAMPAIGN-PAUSED") {
                        router
                            .replace(`/${this.campaignId}/campaign-paused`)
                            .catch(() => {
                                //
                            });
                    }
                    return false;
                }
            } catch (error) {
                console.error("automatic registration failed", {
                    errorType: error instanceof Error ? error.name : "UnknownError",
                });
                return false;
            }
        },
        async dispatchUserEvent(event: {
            campaignId: string;
            event: string;
            eventName?: string;
            identifier: string;
            pageRoute: string;
            pageName?: string;
            pageType?: string;
            journeyStage?: string;
            journeyStep?: string;
            userId?: string;
            sessionId?: string;
            journeyId?: string;
            metadata?: Record<string, unknown>;
        }) {
            this.ensureCorrelationIds();
            // The backend /events validator requires campaignId to be the
            // resolved campaign UUID, not the subdomain/bundle slug that the
            // router exposes via to.params.campaign. Prefer the resolved store
            // UUID and only fall back to the event's value before it resolves.
            const campaignId = this.campaignId || event.campaignId;
            if (!campaignId) {
                console.warn("Skipping user event because campaignId is not available yet", {
                    event: event.event,
                    pageRoute: event.pageRoute,
                });
                return false;
            }
            // Guard against posting a subdomain/bundle slug: the backend
            // validator only accepts a campaign UUID. If the campaign id has
            // not yet resolved to a UUID, skip rather than trigger a 400 — the
            // event is re-emitted once resolution completes.
            const campaignUuidPattern =
                /^(campaign-)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            if (!campaignUuidPattern.test(campaignId)) {
                console.warn("Skipping user event because campaignId is not a resolved UUID yet", {
                    event: event.event,
                    pageRoute: event.pageRoute,
                });
                return false;
            }
            event.campaignId = campaignId;
            this.enrichPayloadWithCanonicalContext(event, {
                path: event.pageRoute,
            });
            const unauthenticatedRoutes = ["Register", "SignIn", "Enter Pin"];
            const currentRouteName = router.currentRoute.value?.name as string | undefined;
            const isAuthenticated = Boolean(
                this.userId &&
                localStorage.getItem("AccessToken") &&
                (!currentRouteName || !unauthenticatedRoutes.includes(currentRouteName))
            );
            const endpoint = isAuthenticated ? `/events/authenticated` : `/events`;
            try {
                const response = await APIClient.post(endpoint, event);
                if (response.success) {
                    return true;
                } else {
                    return false;
                }
            } catch (error) {
                console.error("user event dispatch failed", {
                    errorType: error instanceof Error ? error.name : "UnknownError",
                });
                return false;
            }
        },
    },
});
